import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { NotificationManager, type INotification } from "./NotificationManager";
import i18n from "../language/i18n";
import { Icon } from "../library";
import { notificationProviderStyles } from "./notificationProviderStyles";

const MAX_VISIBLE = 5;
const AUTO_DISMISS_MS = 4000;

const TYPE_CONFIG = {
  success: {
    accent: "ntf-success",
    chip: "ntf-chip-success",
    labelColor: "ntf-label-success",
    progressBg: "ntf-prog-success",
    labelKey: "notification_success",
    icon: <Icon name="checkCircle" size={22} color="currentColor" />,
  },
  error: {
    accent: "ntf-error",
    chip: "ntf-chip-error",
    labelColor: "ntf-label-error",
    progressBg: "ntf-prog-error",
    labelKey: "notification_error",
    icon: <Icon name="xCircle" size={22} color="currentColor" />,
  },
  warning: {
    accent: "ntf-warning",
    chip: "ntf-chip-warning",
    labelColor: "ntf-label-warning",
    progressBg: "ntf-prog-warning",
    labelKey: "notification_warning",
    icon: <Icon name="warning" size={22} color="currentColor" />,
  },
  info: {
    accent: "ntf-info",
    chip: "ntf-chip-info",
    labelColor: "ntf-label-info",
    progressBg: "ntf-prog-info",
    labelKey: "notification_info",
    icon: <Icon name="info" size={22} color="currentColor" />,
  },
};

interface INotificationToastProps {
  notification: INotification;
  onDismiss: (id: number) => void;
}

function NotificationToast({ notification, onDismiss }: INotificationToastProps) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // remaining auto-dismiss budget + the timestamp the current run started, so
  // hovering can pause the countdown and resume it from where it left off.
  const remainingRef = useRef(AUTO_DISMISS_MS);
  const startRef = useRef(0);
  const config =
    (TYPE_CONFIG as Record<string, typeof TYPE_CONFIG.info>)[notification.type] ||
    TYPE_CONFIG.info;

  const handleDismiss = useCallback(() => {
    if (exiting) return;
    setExiting(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    setTimeout(() => onDismiss(notification.id), 200);
  }, [exiting, notification.id, onDismiss]);

  const resume = useCallback(() => {
    if (exiting) return;
    startRef.current = Date.now();
    timerRef.current = setTimeout(handleDismiss, remainingRef.current);
  }, [exiting, handleDismiss]);

  const pause = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startRef.current;
  }, []);

  useEffect(() => {
    resume();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [resume]);

  // The pop-in/out transform lives on this outer shell, which has NO
  // backdrop-filter — so the blurred .ntf-toast surface underneath it is
  // composited once and only the shell's layer is transformed on the GPU,
  // instead of the compositor re-sampling a 32px backdrop blur every frame
  // (× up to MAX_VISIBLE toasts) while the toast slides in.
  return (
    <div className={`ntf-shell ${exiting ? "ntf-anim-out" : "ntf-anim-in"}`}>
      <div
        className={`ntf-toast ${config.accent}`}
        onMouseEnter={pause}
        onMouseLeave={resume}
      >
        <div className={`ntf-chip ${config.chip}`}>{config.icon}</div>

        <div className="ntf-body">
          <div className={`ntf-label ${config.labelColor}`}>
            {i18n.t(config.labelKey)}
          </div>
          <div className="ntf-msg">{notification.message}</div>
        </div>

        <button onClick={handleDismiss} className="ntf-dismiss">
          <Icon name="close" size={14} color="currentColor" />
        </button>

        {!exiting && (
          <div className="ntf-prog-track">
            <div
              className={`${config.progressBg} anim-notification-progress ntf-prog-fill`}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export function NotificationProvider() {
  const [notifications, setNotifications] = useState<INotification[]>([]);

  useEffect(() => {
    NotificationManager._setListener((notification) => {
      setNotifications((prev) => [
        ...prev.slice(-(MAX_VISIBLE - 1)),
        notification,
      ]);
    });
    return () => NotificationManager._setListener(null);
  }, []);

  const handleDismiss = useCallback((id: number) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  if (notifications.length === 0) return null;

  return createPortal(
    <div className="ntf-container">
      <style>{notificationProviderStyles}</style>
      {notifications.map((n) => (
        <div key={n.id} className="ntf-item">
          <NotificationToast notification={n} onDismiss={handleDismiss} />
        </div>
      ))}
    </div>,
    document.body,
  );
}
